import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Appointment, ApptCust, ApptVeh, ApptOps, ApptError, ApptMasked, ViewApptList, UpdateApptDateInp,
    DeteremineDeptInp, OpType, Services, UpdateApptCustInp, UpdateApptOpsInp, UpdateApptVehInp, EmployeeChanges,
    ApptPULoc, ApptDOLoc, ApptLocCom, ZIPSearchReturn } from 'src/models';
import { ViewMode } from 'src/enums';
import { AxiosError } from 'axios';
import * as acBase from 'src/store/states/GenericActionCreator';
import { IStoreState } from 'src/store';
import { checkCustUpdateEmail } from 'src/helpers/DepartmentHelper/DepartmentHelper';

interface ServiceRequests {
    requestComplaint: string,
    requestId: string,
    requestShortDesc: string,
}

interface ApptOpsInp {
    opCode: string,
    opDescription?: string,
    payType: string,
    complaint?: string,
}


interface RecallOps {
    recallType: string,
    recallid: string,
    recallComplaint: string,
}

// interface SearchFields {
//     searchEmail: string;
//     searchPhone: string;
// }

/** Interfaces */
export interface IAppointmentState {
    Appointment: Appointment;
    ApptCust: ApptCust;
    ApptVeh: ApptVeh;
    ApptOps: ApptOps[];
    ViewApptList?: ViewApptList;
    ApptCustFieldsMasked: ApptMasked;
    // SearchFields?: SearchFields;
    error?: ApptError;
    deptChangeFlag?: boolean;
    EmployeeChanges?: EmployeeChanges;
    ApptPULoc: ApptPULoc;
    ApptDOLoc: ApptDOLoc;
    ApptLocCom: ApptLocCom;
    ZIPSearchReturn: ZIPSearchReturn;
    settingHistId: boolean;
}

/** Initial State */
export const initialState: IAppointmentState = {
    Appointment: {
        date: '',
        time: '',
        trans: '',
        advisor: '',
        advisorOptionEN: '',
        advisorOptionFR: '',
        department: '',
        advName: '',
        advImage: '',
        transFlag: '',
        apptNum: '',
    },
    ApptCust: {
        custNameId: '',
        custFirstName: '',
        custLastName: '',
        custEmailAddress: '',
        custPhoneNumber: '',
        custPrefContact: '',
    },
    ApptVeh: {
        vin: '',
        makeCode: '',
        make: '',
        model: '',
        series: '',
        vehYr: '',
        vehColor: '',
        mileage: 0,
        imagePath: '',
    },
    ApptOps: [],
    ApptCustFieldsMasked: {
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
    },
    error: undefined,
    ApptPULoc: {
        puAddress: '',
        puAddress2: '',
        puZipPostal: '',
        puCity: '',
        puStateProvince: '',
    },
    ApptDOLoc: {
        doAddress: '',
        doAddress2: '',
        doZipPostal: '',
        doCity: '',
        doStateProvince: '',
    },
    ApptLocCom: {
        pudoAddComments: '',
    },
    ZIPSearchReturn: {
        city: '',
        state: '',
    },
    deptChangeFlag: false,
    settingHistId: false,
};

export const AppointmentSlice = createSlice({
    name: 'Appointment',
    initialState: initialState,
    reducers: {
        updateAppointmentDate: (state, action: PayloadAction<Appointment>) => {
            state.Appointment = action.payload;
        },
        clearApptState: (state) => {
            state.Appointment = initialState.Appointment;
            state.ApptCust = initialState.ApptCust;
            state.ApptVeh = initialState.ApptVeh;
            state.ApptOps = [];
            state.ViewApptList = undefined;
            // state.SearchFields = undefined;
            state.ApptCustFieldsMasked = initialState.ApptCustFieldsMasked;
            state.EmployeeChanges = undefined;
        },
        clearOnlyApptState: (state) => {
            // Clear only the Appointment state
            state.Appointment = initialState.Appointment;
        },
        updateCustomer: (state, action: PayloadAction<ApptCust>) => {
            state.ApptCust = action.payload;
        },
        updateOps: (state, action: PayloadAction<ApptOps[]>) => {
            state.ApptOps = action.payload;
        },
        updateVeh: (state, action: PayloadAction<ApptVeh>) => {
            state.ApptVeh = action.payload;
        },
        createAppt: (state, action: PayloadAction<IAppointmentState>) => {
            state.Appointment = action.payload.Appointment;
            state.ApptCust = action.payload.ApptCust;
            state.ApptOps = action.payload.ApptOps;
            state.ApptVeh = action.payload.ApptVeh;
            state.EmployeeChanges = undefined;
        },
        apptError: (state, action: PayloadAction<ApptError>) => {
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = undefined;
        },
        getCustAppts: (state, action: PayloadAction<ViewApptList>) => {
            state.ViewApptList = action.payload;
        },
        determineDept: (state, action: PayloadAction<Appointment>) => {
            state.Appointment = action.payload;
        },
        editAppt: (state, action: PayloadAction<IAppointmentState>) => {
            state.Appointment = action.payload.Appointment;
            state.ApptCust = action.payload.ApptCust;
            state.ApptOps = action.payload.ApptOps;
            state.ApptVeh = action.payload.ApptVeh;
        },
        updateVehicleMileage: (state, action: PayloadAction<number>) => {
            state.ApptVeh.mileage = action.payload;
        },
        cancelAppt: (state, action: PayloadAction<IAppointmentState>) => {
            state.Appointment = action.payload.Appointment;
            state.ApptCust = action.payload.ApptCust;
            state.ApptOps = action.payload.ApptOps;
            state.ApptVeh = action.payload.ApptVeh;
        },
        updateMaskedFields: (state, action: PayloadAction<ApptMasked>) => {
            state.ApptCustFieldsMasked = action.payload;
        },
        // updateSearchFields: (state, action: PayloadAction<SearchFields>) => {
        //     state.SearchFields = action.payload;
        // },
        setChangeDeptFlag: (state, action: PayloadAction<boolean>) => {
            state.deptChangeFlag = action.payload;
        },
        updateEmployeeChanges: (state, action: PayloadAction<EmployeeChanges>) => {
            state.EmployeeChanges = action.payload;
        },
        updatePickupFields: (state, action: PayloadAction<ApptPULoc>) => {
            state.ApptPULoc = action.payload;
        },
        updateDeliveryFields: (state, action: PayloadAction<ApptDOLoc>) => {
            state.ApptDOLoc = action.payload;
        },
        updateLocationComments: (state, action: PayloadAction<ApptLocCom>) => {
            state.ApptLocCom = action.payload;
        },
        clearPuDoInfo: (state) => {
            state.ApptPULoc = initialState.ApptPULoc;
            state.ApptDOLoc = initialState.ApptDOLoc;
            state.ApptLocCom = initialState.ApptLocCom;
        },
        zipSearch: (state, action: PayloadAction<ZIPSearchReturn>) => {
            state.ZIPSearchReturn = action.payload;
        },
        clearZipSearch: (state) => {
            state.ZIPSearchReturn = initialState.ZIPSearchReturn;
        },
        toggleApptLangLits: (state, action: PayloadAction<Appointment>) => {
            state.Appointment = action.payload;
        },
        setApptHistId: (state, action: PayloadAction<string>) => {
            state.ApptCust.custNameId = action.payload;
            state.settingHistId = false;
        },
        setApptHistIdTracking: (state, action: PayloadAction<boolean>) => {
            state.settingHistId = action.payload;
        },
        setApptNum: (state, action: PayloadAction<string>) => {
            state.Appointment.apptNum = action.payload;
        },
    },
});

export const {
    updateAppointmentDate,
    clearApptState,
    clearOnlyApptState,
    updateCustomer,
    updateOps,
    updateVeh,
    createAppt,
    apptError,
    clearError,
    getCustAppts,
    determineDept,
    editAppt,
    updateVehicleMileage,
    cancelAppt,
    updateMaskedFields,
    // updateSearchFields,
    setChangeDeptFlag,
    updateEmployeeChanges,
    updatePickupFields,
    updateDeliveryFields,
    updateLocationComments,
    clearPuDoInfo,
    zipSearch,
    clearZipSearch,
    toggleApptLangLits,
    setApptHistId,
    setApptHistIdTracking,
    setApptNum,
} = AppointmentSlice.actions;

export default AppointmentSlice.reducer;

export function UpdateApptDate(inp: UpdateApptDateInp) {
    return (dispatch, getState: () => IStoreState) => {
        const CurrAppt = { ...getState().Appointment.Appointment };
        let advImage = inp.selAdvImage;
        if (advImage === undefined) {
            advImage = '';
        }
        let newTransFlag = inp.selTransFlag;
        if (newTransFlag === undefined) {
            newTransFlag = '';
        }
        const Appointment = {
            date: inp.selDate || CurrAppt.date,
            time: inp.selTime || CurrAppt.time,
            advisor: inp.selAdv === '' ? '' : inp.selAdv || CurrAppt.advisor,
            advisorOptionEN: CurrAppt.advisorOptionEN,
            advisorOptionFR: CurrAppt.advisorOptionFR,
            trans: inp.selTrans || CurrAppt.trans,
            department: CurrAppt.department,
            advName: inp.selAdvName === '' ? '' : inp.selAdvName || CurrAppt.advName,
            advImage: advImage === '' ? '' : advImage || CurrAppt.advImage,
            // Reservations/ Appointments do not have a transFlag
            transFlag: newTransFlag === '' ? '' : newTransFlag || CurrAppt.transFlag,
            apptNum: inp.selApptNum !== undefined && inp.selApptNum !== '' ? inp.selApptNum : CurrAppt.apptNum,
        };
        dispatch(updateAppointmentDate(Appointment));
    };
}

export function UpdateEmployeeChanges(inp: EmployeeChanges) {
    return (dispatch) => {
        const changes = {
            empFirst: inp.empFirst ?? '',
            empLast: inp.empLast ?? '',
            empPhone: inp.empPhone ?? '',
            empEmail: inp.empEmail ?? '',
        };
        dispatch(updateEmployeeChanges(changes));
    };
}

export function ClearApptState() {
    return (dispatch) => {
        dispatch(clearApptState());
    };
}

export function ClearOnlyApptState() {
    return (dispatch) => {
        dispatch(clearOnlyApptState());
    };
}

// This function will update the Appointment Customer data
// If a NameID is not defined or null, then we will use the one already in state.
export function UpdateApptCust(inp: UpdateApptCustInp) {
    return (dispatch) => {
        const newNameId = inp.nameId;
        const ApptCust = {
            custNameId: newNameId,
            custFirstName: inp.firstName,
            custLastName: inp.lastName,
            custEmailAddress: inp.emailAddress,
            custPhoneNumber: inp.phoneNumber,
            custPhoneType: inp.phoneType,
            custPrefContact: inp.prefContact,
            custLoggedIn: inp.loggedIn,
        };

        // const stateSearchFields = getState().Appointment.SearchFields;
        // let emailAddress = inp.emailAddress;
        // let phoneNumber = inp.phoneNumber;
        // if (stateSearchFields) {
        //     emailAddress = getState().Appointment.SearchFields?.searchEmail ?? emailAddress;
        //     phoneNumber = getState().Appointment.SearchFields?.searchPhone ?? phoneNumber;
        // }

        // let SearchFields = {
        //     searchEmail: emailAddress,
        //     searchPhone: phoneNumber,
        // };
        // if (stateSearchFields) {
        //     SearchFields = stateSearchFields;
        // }
        dispatch(updateCustomer(ApptCust));
        // dispatch(updateSearchFields(SearchFields));
    };
}

export function SearchCust(selectedNum: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;

        const profileNum = getState().Framework.specProfile;
        const emailAddress = getState().Appointment.ApptCust.custEmailAddress;
        const phoneNumber = getState().Appointment.ApptCust.custPhoneNumber;
        const firstName = getState().Appointment.ApptCust.custFirstName;
        const lastName = getState().Appointment.ApptCust.custLastName;

        // const stateSearchFields = getState().Appointment.SearchFields;
        // const identityVerified = getState().Customer.identityVerified;
        // if (stateSearchFields) {
        //     emailAddress = getState().Appointment.SearchFields?.searchEmail ?? emailAddress;
        //     phoneNumber = getState().Appointment.SearchFields?.searchPhone ?? phoneNumber;
        // }

        // let hideData = true;
        // const login = getState().Customer.Customer.login;
        // if (login) {
        //     hideData = false;
        // }
        let nameId = getState().Customer.Customer.nameId;
        const selectedVehicle = getState().Vehicles.selectedVehicle;
        if (selectedVehicle) {
            const vehicle = selectedVehicle ? getState().Vehicles.vehicles[parseInt(selectedVehicle)] : null;
            if (vehicle) {
                nameId = vehicle.nameId;
            }
        }
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        const reqData = {
            email: emailAddress,
            phone: phoneNumber,
            firstName: '',
            lastName: '',
            profile: profileNum,
            logId: logId,
            nameId: selectedNum,
            allowMulti: 'N',
            callingProgram: callingProgram,
            // hideData: hideData,
            // identityVerified: identityVerified,
            // selectedNum: selectedNum,
        };

        const response = await acBase.eraWebServiceCall('SVPController', 'CustSearch', reqData, store, branch);
        if (response.retCode === '8000') {
            const error = {
                errorCode: response.retCode,
                errorMsg: response.errorMsg,
            };
            dispatch(apptError(error));
        } else {
            const email = emailAddress === '' ? response.emailAddress : emailAddress;
            const phone = phoneNumber === '' ? response.phoneNumber : phoneNumber;

            const ApptCust = {
                // custNameId: response.nameId,
                custNameId: nameId,
                custFirstName: response.firstName ?? firstName,
                custLastName: response.lastName ?? lastName,
                custEmailAddress: email,
                custPhoneNumber: phone,
                custPhoneType: response.phoneType,
                custPrefContact: response.prefContact,
                custLoggedIn: false,
            };

            const ApptMasked = {
                firstName: false,
                lastName: response.lastName?.includes('*'),
                phone: phone?.includes('*'),
                email: email?.includes('*'),
            };
            // let SearchFields = {
            //     searchEmail: emailAddress,
            //     searchPhone: phoneNumber,
            // };
            // if (stateSearchFields) {
            //     SearchFields = stateSearchFields;
            // }

            dispatch(updateCustomer(ApptCust));
            dispatch(updateMaskedFields(ApptMasked));
            // dispatch(updateSearchFields(SearchFields));
        }
    };
}

export function UpdateMaskedFields(inp: ApptMasked) {
    return (dispatch) => {
        dispatch(updateMaskedFields(inp));
    };
}

// This function will clear the Appointment Customer data
export function ClearApptCust() {
    return (dispatch) => {
        dispatch(updateCustomer(initialState.ApptCust));
    };
}

// This function will update the Appointment Vehicle data
export function UpdateApptVehicle(inp: UpdateApptVehInp) {
    return (dispatch) => {
        const ApptVeh = {
            vin: inp.vin,
            makeCode: inp.makeCode,
            make: inp.make,
            model: inp.model,
            series: inp.series,
            vehYr: inp.vehYr,
            vehColor: inp.vehColor,
            mileage: inp.mileage,
            imagePath: inp.imagePath,
        };
        dispatch(updateVeh(ApptVeh));
    };
}

// This function will update the Appointment Vehicle mileage
export function UpdateApptVehicleMileage(mileage: number) {
    return (dispatch) => {
        dispatch(updateVehicleMileage(mileage));
    };
}

// This function will update the update job/opcode data
export function UpdateApptOps(inp: UpdateApptOpsInp[]) {
    // If a price limit is passed and it is undefined, replace with a blank string
    const options = inp;
    options.forEach((option) => {
        if (option?.itemCostHigh === undefined) {
            option.itemCostHigh = '';
        }
        if (option?.itemCostLow === undefined) {
            option.itemCostLow = '';
        }
    });

    return (dispatch) => {
        dispatch(updateOps(options));
    };
}

// Call PICK to add the appointment.
export function CreateAppt(inp: Services[], optIn: boolean, makePrefContact: string, sendConfirmation: string, isOEM: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const appointment = { ...getState().Appointment};
        const menus = getState().Menus;
        const specs = getState().Specs.specs;
        const profileNum = getState().Framework.specProfile;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const previewFlag = getState().Framework.previewFlag;
        const logId = getState().Specs.specs?.logId;
        const serviceRequests: ServiceRequests[] = [];
        const recall: RecallOps[] = [];
        const lexusTracking = getState().Specs.specs?.lexusTrackingFlag;
        const customer = { ...getState().Customer};
        const employeeFlag = customer.Customer.employeeFlag;
        const doneElsewhere = getState().AdditionalServices.doneElsewhere.map(DNE => DNE.opCode);
        const pickupInfo = getState().Appointment.ApptPULoc;
        const deliveryInfo = getState().Appointment.ApptDOLoc;
        const locationComments = getState().Appointment.ApptLocCom;
        const gmossVersion = getState().Framework.gmossVersion;
        const kiaTrackingFlag = getState().Specs.specs?.kiaTrackingFlag;
        const vwTrackingFlag = getState().Specs.specs?.vwTrackingFlag;
        const addlTrackToken = getState().Framework.addlTrackToken;
        const idkMileageCheck = getState().Framework.idkMileage;
        const vwVariant = getState().Framework.vwVariant;
        const vwProvider = getState().Framework.vwProvider;
        const vwKeyword = getState().Framework.vwKeyword;
        // const toyotaTracking = getState().Specs.specs?.toyotaTrackingFlag;
        let vinOwned = getState().Vehicles.decodedData.decodeErr;
        if (vinOwned?.split(':')[0] !== 'VINOWNED') {
            vinOwned = '';
        }
        let servicesSkipped = '';
        const apptOps: ApptOpsInp[] = [];
        let recomAdded = '';
        let writeYourOwnAdded = '';
        let diagnosisPerformed = '';
        // If the selected item was removed from Services in the Summary screen do not add it to the appointment
        // The skip service op is not included in the service screen, we will add it if no other ops exist
        const operations = appointment.ApptOps.filter((op) => inp.find(
            service => (service.id === op.id)) || (op.opType === OpType.SkipServiceReason));
        operations.forEach((op) => {
            if (op.opType === OpType.WriteYourOwn) {
                serviceRequests.push({
                    requestComplaint: op.operation.replace(/'/g, '&quot'),
                    requestId: op.id,
                    requestShortDesc: op.concern.replace(/'/g, '&quot'),
                });
                writeYourOwnAdded = 'Y';
            } else if (op.opType === OpType.Diagnosis && op.id.substr(0, 4)) {
                serviceRequests.push({
                    requestComplaint: op.operation.replace(/'/g, '&quot'),
                    requestId: op.id,
                    requestShortDesc: op.concern.replace(/'/g, '&quot'),
                });
                diagnosisPerformed = 'Y';
            } else if (op.opType === OpType.RecommendedService) {
                apptOps.push({
                    opCode: op.operation,
                    opDescription: op.concern,
                    payType: 'C',
                });
                recomAdded = 'Y';
            } else if (op.opType === OpType.Recall) {
                recall.push({
                    recallComplaint: op.concern,
                    recallid: op.id,
                    recallType: op.operation,
                });
            } else if (op.opType === OpType.IndividualService) {
                apptOps.push({
                    opCode: op.operation,
                    opDescription: op.concern,
                    payType: 'C',
                });
            } else if (op.opType === OpType.WeOwe) {
                serviceRequests.push({
                    requestComplaint: op.operation,
                    requestId: op.id,
                    requestShortDesc: op.concern,
                });
            } else if (op.opType === OpType.SkipServiceReason && operations.length === 1) {
                servicesSkipped='Y';
                if (specs?.skipSVCOp) {
                    apptOps.push({
                        opCode: specs.skipSVCOp,
                        opDescription: op.concern.substr(0, 20).replace(/'/g, '&quot'),
                        payType: 'C',
                        complaint: op.concern.replace(/'/g, '&quot'),
                    });
                } else {
                    serviceRequests.push({
                        requestComplaint: '',
                        requestId: op.id,
                        requestShortDesc: op.concern.replace(/'/g, '&quot'),
                    });
                }
            }
        });

        const strippedPhone = appointment.ApptCust.custPhoneNumber.replace(/[^0-9]/g, '');
        let vin = appointment.ApptVeh.vin;
        // If the VIN is set to "Unknown" in the appointment state set it to blank
        if (vin === 'Unknown') {
            vin = '';
        }

        let phoneType = '';
        if (appointment.ApptCust.custPhoneType) {
            if (appointment.ApptCust.custPhoneType === 'R') {
                phoneType = 'H';
            }
            phoneType = appointment.ApptCust.custPhoneType;
        }

        // Build a string of the checked conditional ops to pass to ERA
        const checkedConditionalMenus: string[] = [];
        const conditionalMenuDescs: string[] = [];
        const conditionalMenuEraOps: string[] = [];
        menus.checkedConditionalMenus.forEach((menu) => {
            checkedConditionalMenus.push(menu.id);
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menu.id);
            conditionalMenuDescs.push(menus.allMenus.menus[menuIndex].title);
            conditionalMenuEraOps.push(menus.allMenus.menus[menuIndex].eraOpCode);
        });

        let mileageMenuDesc = '';
        let mileageMenuEraOp = '';
        if (menus.checkedMileageMenu) {
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menus.checkedMileageMenu);
            mileageMenuDesc = menus.allMenus.menus[menuIndex].title;
            mileageMenuEraOp = menus.allMenus.menus[menuIndex].eraOpCode;
        }
        const sessionID = getState().Framework.sessionId;
        const vehSeries = appointment.ApptVeh.series ? appointment.ApptVeh.series.replace(/'/g, '&quot') : null;
        const selectedVehicle = getState().Vehicles?.selectedVehicle!;
        let lastAdv = '';
        if (selectedVehicle) {
            lastAdv = getState().Vehicles.vehicles[selectedVehicle].lastAdv;
        }
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        // If this account is tied to an employee, then updating the account information will update
        // the employee's NAM file.  Put updates to account information for employees in comments.
        const comments = vinOwned ? vinOwned : '';
        let firstName = appointment.ApptCust.custFirstName.replace(/'/g, '');
        let lastName = appointment.ApptCust.custLastName.replace(/'/g, '');
        let email = appointment.ApptCust.custEmailAddress;
        let phoneCell = strippedPhone;
        const employeeInfo: EmployeeChanges = appointment.EmployeeChanges ?? {};
        if (employeeFlag) {
            if (employeeInfo?.empFirst) {
                firstName = customer.Customer.firstName.replace(/'/g, '');
            }
            if (employeeInfo?.empLast) {
                lastName = customer.Customer.lastName.replace(/'/g, '');
            }
            if (employeeInfo?.empEmail) {
                email = customer.Customer.emailAddress;
            }
            if (employeeInfo?.empPhone) {
                phoneCell = customer.Customer.phoneNumber.replace(/[^0-9]/g, '');
            }
        }

        const TMNAMileage = getState().AdditionalServices.maintContractData.TMNAMileage;
        const prevMileage = getState().Vehicles.vehicles?.[selectedVehicle]?.prevMileage ?? 0;

        let sdProviderId = '';
        if (lexusTracking === 'Y') {
            sdProviderId = 'REYNOLDS';
        }
        // Double check that trans type is correct, clears info if not
        if (appointment.Appointment.transFlag !== 'P' && appointment.Appointment.transFlag !== 'U' &&
            gmossVersion !== '1.4.2') {
            dispatch(clearPuDoInfo());
        }
        // Add token and 'I do not know mileage' values to Appointment record (Kia Tracking only).
        let oemToken = '';
        let idkMileage = '';
        if (kiaTrackingFlag) {
            oemToken = addlTrackToken;
            idkMileage = idkMileageCheck;
        }
        // Add Variant, Provider, and Keyword values to Appointment record (VW Deep Link)
        let variant = '';
        let provider = '';
        let keyword = '';
        if (vwTrackingFlag && (vwVariant || vwProvider || vwKeyword)) {
            variant = vwVariant;
            provider = vwProvider;
            keyword = vwKeyword;
        }
        const reqData = {
            // Service Requests
            previewFlag: previewFlag,
            logId: logId,
            writeYourOwn: serviceRequests,
            clientId: 'RR',
            // Main appt data
            apptNumber: '',
            advNumber: appointment.Appointment.advisor,
            dispatcherNumber: '',
            apptDate: appointment.Appointment.date,
            apptTime: appointment.Appointment.time,
            promiseDate: '',
            promiseTime: '',
            department: appointment.Appointment.department,
            apptFlag: '',
            vehPriority: '',
            vehPriorityPassword: '',
            transportationType: appointment.Appointment.transFlag,
            modifyJobsFlag: 'N',
            allowOverbookFlag: '',
            apptServiceLane: '',
            // VW Deep Link Tracking Data
            vwVariant: variant,
            vwProvider: provider,
            vwKeyword: keyword,
            // VehicleData attributes
            vin: vin,
            mileage: appointment.ApptVeh.mileage === -1 ? TMNAMileage > prevMileage ? TMNAMileage.toString() :
                prevMileage.toString() : appointment.ApptVeh.mileage.toString(),
            lastAdv: lastAdv,
            oemToken: oemToken,
            idkMileage: idkMileage,
            // Appt CustomerData attributes
            custNumber: appointment.ApptCust.custNameId,
            accountPassword: '',
            // Comments attributes
            comments: comments,
            // Estimate attributes
            partsEstime: '',
            laborEstimate: '',
            totalEstimate: '',
            // CustomerData attributes
            firstName: firstName,
            lastName: lastName,
            midInitial: '',
            salutation: '',
            phoneHome: '',
            emailAddress: email,
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            phoneBusiness: '',
            phoneBusinessExt: '',
            phoneCell: phoneCell,
            optIn: optIn ? 'Y' : '',
            sendConfirmation: sendConfirmation,
            phoneType: phoneType,
            makePrefContact: makePrefContact,
            // I individual
            IBFlag: 'I',
            // VehicleData attributes
            vehYear: appointment.ApptVeh.vehYr,
            vehMake: appointment.ApptVeh.make,
            vehModel: appointment.ApptVeh.model,
            // vehSeries: appointment.ApptVeh.series.replace(/'/g, '&quot'),
            vehSeries: vehSeries,
            vehColor: appointment.ApptVeh.vehColor,
            // Appointment Operations
            apptOps: apptOps,
            // Recall
            recall: recall,
            profileNum: profileNum,
            // Maintenance Menus
            selectedScheduleOption: menus.selectedScheduleOption,
            checkedMileageMenu: menus.checkedMileageMenu,
            checkedMileageMenuDesc: mileageMenuDesc,
            checkedMileageMenuEraOp: mileageMenuEraOp,
            checkedConditionalMenus: checkedConditionalMenus,
            checkedConditionalMenusDesc: conditionalMenuDescs,
            checkedConditionalMenusEraOp: conditionalMenuEraOps,
            sessionID: sessionID,
            servicesSkipped: servicesSkipped,
            recomAdded: recomAdded,
            doneElsewhere: doneElsewhere,
            writeYourOwnAdded: writeYourOwnAdded,
            diagnosisPerformed: diagnosisPerformed,
            callingProgram: callingProgram,
            sdProviderId: sdProviderId,
            employeeChangeFirst: employeeInfo?.empFirst ?? '',
            employeeChangeLast: employeeInfo?.empLast ?? '',
            employeeChangeEmail: employeeInfo?.empEmail ?? '',
            employeeChangePhone: employeeInfo?.empPhone ?? '',
            isOEM: isOEM,
            // Pickup and Delivery Info
            puAddress: pickupInfo?.puAddress,
            puAddress2: pickupInfo?.puAddress2,
            puCity: pickupInfo?.puCity,
            puStateProv: pickupInfo?.puStateProvince,
            puZipPostal: pickupInfo?.puZipPostal,
            doAddress: deliveryInfo?.doAddress,
            doAddress2: deliveryInfo?.doAddress2,
            doCity: deliveryInfo?.doCity,
            doStateProv: deliveryInfo?.doStateProvince,
            doZipPostal: deliveryInfo?.doZipPostal,
            pudoComments: locationComments?.pudoAddComments.replace(/'/g, '&quot'),
        };
        const response = await acBase.eraWebServiceCall<object>('SVPController', 'AddAppt', reqData, store, branch);
        let error: ApptError = { errorCode: '', errorMsg: '' };
        if (response && (response as AxiosError).isAxiosError) {
            error = {
                errorCode: (response as AxiosError).code ?? '',
                errorMsg: (response as AxiosError).message,
            };
        } else if (response && response.errorCode) {
            error = {
                errorCode: response.errorCode,
                errorMsg: response.errorMsg,
            };
        }
        if (response.custNameId) {
            const ApptCust = { ...appointment.ApptCust, custNameId: response.custNameId};
            appointment.ApptCust = ApptCust;
        }
        const apptNumb = response.ApptNumber;
        dispatch(createAppt(appointment));
        dispatch(setApptNum(apptNumb));
        dispatch(apptError(error));
        dispatch(setApptNum(apptNumb));
    };
}

export function ClearApptError() {
    return (dispatch) => {
        dispatch(clearError());
    };
}

export function GetCustAppts(custID: string, profile: string, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const apptNo = getState().Framework.loadAppt;
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            custID: custID,
            profile: profile,
            apptNo: apptNo,
            logId: logId,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<ViewApptList>('SVPController', 'GetCustAppts', reqData, store, branch);
        let appointmentList = [];
        if (response) {
            if (response.errorCode === '8000') {
                const error = {
                    errorCode: response.errorCode,
                    errorMsg: response.errorMsg,
                };
                dispatch(apptError(error));
            } else {
                appointmentList = response;
                dispatch(getCustAppts(appointmentList));
            }
        }
    };
}
// Determine the Appointment Department based on what jobs the user has selected.
export function DetermineDept(inp: DeteremineDeptInp) {
    // Now go though each selected job and change the deaprtment as needed.
    // If there are no selected jobs then default department based on the specs
    return (dispatch, getState: () => IStoreState) => {
        const CurrAppt = { ...getState().Appointment.Appointment };
        const CurrApptOps = { ...getState().Appointment.ApptOps };
        const specs = getState().Specs.specs;
        const apptCustState = getState().Appointment.ApptCust;
        let custEmail = getState().Customer.Customer.emailAddress;
        let anyAdvisorEN = getState().Specs.specs?.svcSpecs?.advisorOptionEN ?? '';
        let anyAdvisorFR = getState().Specs.specs?.svcSpecs?.advisorOptionFR ?? '';
        custEmail = custEmail ? custEmail : getState().Appointment.ApptCust.custEmailAddress;

        let varDepartment = inp.defaultDept;
        let workingDept = '';
        for (const x in CurrApptOps) {
            const opDepartment = CurrApptOps[x].department;
            // If this is the first job or all jobs have the same department use the job's department
            // Otherwise if there is a combination of different departments default to service
            switch (workingDept) {
                case '':
                    workingDept = opDepartment;
                    break;
                case opDepartment:
                    break;
                default: {
                    if (specs?.svcSpecs) {
                        workingDept = 'S';
                    } else if (specs?.ossSpecs) {
                        workingDept = 'Q';
                    } else if (specs?.bdySpecs) {
                        workingDept = 'B';
                    }
                }
            }
        }
        if (workingDept !== '') {
            varDepartment = workingDept;
        }
        switch (varDepartment) {
            case 'S':
                break;
            case 'B':
                anyAdvisorEN = getState().Specs.specs?.bdySpecs?.advisorOptionEN ?? '';
                anyAdvisorFR = getState().Specs.specs?.bdySpecs?.advisorOptionFR ?? '';
                break;
            case 'Q':
                anyAdvisorEN = getState().Specs.specs?.ossSpecs?.advisorOptionEN ?? '';
                anyAdvisorFR = getState().Specs.specs?.ossSpecs?.advisorOptionFR ?? '';
                break;
            default:
                anyAdvisorEN = '';
                anyAdvisorFR = '';
        }
        const Appointment = {
            date: CurrAppt.date,
            time: CurrAppt.time,
            trans: CurrAppt.trans,
            advisor: CurrAppt.advisor,
            department: varDepartment,
            advName: CurrAppt.advName,
            advisorOptionEN: anyAdvisorEN,
            advisorOptionFR: anyAdvisorFR,
            advImage: CurrAppt.advImage,
            transFlag: CurrAppt.transFlag,
            apptNum: CurrAppt.apptNum,
        };
        const custEmailUpdate = checkCustUpdateEmail(varDepartment, specs);
        if (!custEmailUpdate) {
            const apptCust = {...apptCustState, custEmailAddress: custEmail };
            dispatch(updateCustomer(apptCust));
        }
        dispatch(determineDept(Appointment));
    };
}

// Call PICK to edit the appointment.
export function EditAppt(inp: Services[], optIn: boolean, makePrefContact: string, sendConfirmation: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const appointment = { ...getState().Appointment };
        const profileNum = getState().Framework.specProfile;
        const menus = getState().Menus;
        const specs = getState().Specs.specs;
        const employeeFlag = getState().Customer.Customer.employeeFlag;
        const customer = {...getState().Customer};
        const originalOps = appointment.ViewApptList?.find(appt => appt.apptNum === appointment.Appointment.apptNum)?.ApptOps;
        const doneElsewhere = getState().AdditionalServices.doneElsewhere.map(DNE => DNE.opCode);
        const pickupInfo = getState().Appointment.ApptPULoc;
        const deliveryInfo = getState().Appointment.ApptDOLoc;
        const locationComments = getState().Appointment.ApptLocCom;
        const gmossVersion = getState().Framework.gmossVersion;
        // Find what operations were deleted
        const deleteJobNums: string[] = [];
        const deleteMenus: string[] = [];
        // Loop through the viewApptList operations and check if any are missing in the appointment state
        originalOps?.forEach((origOp, index) => {
            if (appointment.ApptOps.findIndex(op => op.id === index.toString() ||
                ((op.operation === origOp.opCode && op.operation !== '') && (op.concern === origOp.opDesc))) === -1) {
                if ((origOp.opCode === 'MMENU') || (origOp.opCode === 'CMENU') || (origOp.opCode === 'SMENU')) {
                    deleteMenus.push(origOp.opDesc);
                } else {
                    deleteJobNums.push((index + 1).toString());
                }
            }
        });
        // Update the appointment state without the deleted operations
        appointment.ApptOps = appointment.ApptOps.filter((op) => inp.find(service => service.id === op.id));
        const strippedPhone = appointment.ApptCust.custPhoneNumber?.replace(/[^0-9]/g, '');
        let phoneType = '';
        if (appointment.ApptCust.custPhoneType) {
            if (appointment.ApptCust.custPhoneType === 'R') {
                phoneType = 'H';
            }
            phoneType = appointment.ApptCust.custPhoneType;
        }
        const logId = getState().Specs.specs?.logId;
        const serviceRequests: ServiceRequests[] = [];
        const recall: RecallOps[] = [];
        let servicesSkipped = '';
        const apptOps: ApptOpsInp[] = [];
        let recomAdded = '';
        let writeYourOwnAdded = '';
        let diagnosisPerformed = '';
        // If the selected item was removed from Services in the Summary screen do not add it to the appointment
        // The skip service op is not included in the service screen, we will add it if no other ops exist
        const operations = appointment.ApptOps.filter((op) => inp.find(
            service => (service.id === op.id)) || (op.opType === OpType.SkipServiceReason));
        operations.forEach((op) => {
            if (op.opType === OpType.WriteYourOwn && op.id.substr(0, 4)) {
                serviceRequests.push({
                    requestComplaint: op.operation.replace(/'/g, '&quot'),
                    requestId: op.id,
                    requestShortDesc: op.concern.replace(/'/g, '&quot'),
                });
                writeYourOwnAdded = 'Y';
            } else if (op.opType === OpType.Diagnosis && op.id.substr(0, 4)) {
                serviceRequests.push({
                    requestComplaint: op.operation.replace(/'/g, '&quot'),
                    requestId: op.id,
                    requestShortDesc: op.concern.replace(/'/g, '&quot'),
                });
                diagnosisPerformed = 'Y';
            } else if (op.opType === OpType.RecommendedService) {
                apptOps.push({
                    opCode: op.operation,
                    opDescription: op.concern,
                    payType: 'C',
                });
                recomAdded = 'Y';
            } else if (op.opType === OpType.Recall) {
                recall.push({
                    recallComplaint: op.concern,
                    recallType: op.operation,
                    recallid: op.id,
                });
            } else if (op.opType === OpType.IndividualService) {
                apptOps.push({
                    opCode: op.operation,
                    opDescription: op.concern,
                    payType: 'C',
                });
            } else if (op.opType === OpType.WeOwe) {
                serviceRequests.push({
                    requestComplaint: op.operation,
                    requestId: op.id,
                    requestShortDesc: op.concern,
                });
            } else if (op.opType === OpType.SkipServiceReason && operations.length === 1) {
                servicesSkipped='Y';
                if (specs?.skipSVCOp) {
                    apptOps.push({
                        opCode: specs.skipSVCOp,
                        opDescription: op.concern.substr(0, 20).replace(/'/g, '&quot'),
                        payType: 'C',
                        complaint: op.concern.replace(/'/g, '&quot'),
                    });
                } else {
                    serviceRequests.push({
                        requestComplaint: '',
                        requestId: op.id,
                        requestShortDesc: op.concern.replace(/'/g, '&quot'),
                    });
                }
            }
        });

        // Build a string of the checked conditional ops to pass to ERA
        const checkedConditionalMenus: string[] = [];
        const conditionalMenuDescs: string[] = [];
        const conditionalMenuEraOps: string[] = [];
        menus.checkedConditionalMenus.forEach((menu) => {
            checkedConditionalMenus.push(menu.id);
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menu.id);
            conditionalMenuDescs.push(menus.allMenus.menus[menuIndex].title);
            conditionalMenuEraOps.push(menus.allMenus.menus[menuIndex].eraOpCode);
        });

        let mileageMenuDesc = '';
        let mileageMenuEraOp = '';
        if (menus.checkedMileageMenu) {
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menus.checkedMileageMenu);
            mileageMenuDesc = menus.allMenus.menus[menuIndex].title;
            mileageMenuEraOp = menus.allMenus.menus[menuIndex].eraOpCode;
        }
        const previewFlag = getState().Framework.previewFlag;

        const vehSeries = appointment.ApptVeh.series ? appointment.ApptVeh.series.replace(/'/g, '&quot') : null;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        // If this account is tied to an employee, then updating the account information will update
        // the employee's NAM file.  Put updates to account information for employees in employeeChanges.
        let firstName = appointment.ApptCust.custFirstName.replace(/'/g, '');
        let lastName = appointment.ApptCust.custLastName.replace(/'/g, '');
        let email = appointment.ApptCust.custEmailAddress;
        let phoneCell = strippedPhone;
        const employeeInfo: EmployeeChanges = appointment.EmployeeChanges ?? {};
        if (employeeFlag) {
            if (employeeInfo?.empFirst) {
                firstName = customer.Customer.firstName.replace(/'/g, '');
            }
            if (employeeInfo?.empLast) {
                lastName = customer.Customer.lastName.replace(/'/g, '');
            }
            if (employeeInfo?.empEmail) {
                email = customer.Customer.emailAddress;
            }
            if (employeeInfo?.empPhone) {
                phoneCell = customer.Customer.phoneNumber.replace(/[^0-9]/g, '');
            }
        }
        // Double check that trans type is correct, clears info if not
        if (appointment.Appointment.transFlag !== 'P' && appointment.Appointment.transFlag !== 'U' &&
            gmossVersion !== '1.4.2') {
            dispatch(clearPuDoInfo());
        }
        const reqData = {
            // Service Requests
            logId: logId,
            writeYourOwn: serviceRequests,
            clientId: 'RR',
            // Main appt data
            apptNumber: appointment.Appointment.apptNum,
            advNumber: appointment.Appointment.advisor,
            apptDate: appointment.Appointment.date,
            apptTime: appointment.Appointment.time,
            department: appointment.Appointment.department,
            apptFlag: '',
            vehPriority: '',
            vehPriorityPassword: '',
            transportationType: appointment.Appointment.transFlag,
            modifyJobsFlag: 'Y',
            allowOverbookFlag: 'Y',
            apptServiceLane: '',
            // VehicleData attributes
            vin: appointment.ApptVeh.vin,
            mileage: appointment.ApptVeh.mileage.toString(),
            // Appt CustomerData attributes
            custNumber: appointment.ApptCust.custNameId,
            // CustomerData attributes
            // firstName: appointment.ApptCust.custFirstName.replace(/'/g, ''),
            // lastName: appointment.ApptCust.custLastName.replace(/'/g, ''),
            // emailAddress: appointment.ApptCust.custEmailAddress,
            // phoneCell: strippedPhone,
            firstName: firstName,
            lastName: lastName,
            emailAddress: email,
            phoneCell: phoneCell,
            optIn: optIn ? 'Y' : '',
            sendConfirmation: sendConfirmation,
            phoneType: phoneType,
            makePrefContact: makePrefContact,
            // I individual
            IBFlag: 'I',
            // VehicleData attributes
            vehYear: appointment.ApptVeh.vehYr,
            vehMake: appointment.ApptVeh.make,
            vehModel: appointment.ApptVeh.model,
            // vehSeries: appointment.ApptVeh.series.replace(/'/g, '&quot'),
            vehSeries: vehSeries,
            vehColor: appointment.ApptVeh.vehColor,
            // Appointment Operations
            apptOps: apptOps,
            // Recall
            recall: recall,
            // Spec Data
            profileNum: profileNum,
            // Deleted jobs
            deleteJobNums: deleteJobNums,
            deleteMenus: deleteMenus,
            // Maintenance Menus
            selectedScheduleOption: menus.selectedScheduleOption,
            checkedMileageMenu: menus.checkedMileageMenu,
            checkedMileageMenuDesc: mileageMenuDesc,
            checkedMileageMenuEraOp: mileageMenuEraOp,
            checkedConditionalMenus: checkedConditionalMenus,
            checkedConditionalMenusDesc: conditionalMenuDescs,
            checkedConditionalMenusEraOp: conditionalMenuEraOps,
            servicesSkipped: servicesSkipped,
            recomAdded: recomAdded,
            doneElsewhere: doneElsewhere,
            writeYourOwnAdded: writeYourOwnAdded,
            diagnosisPerformed: diagnosisPerformed,
            previewFlag: previewFlag,
            callingProgram: callingProgram,
            // Employee Record Changes
            employeeChangeFirst: employeeInfo?.empFirst ?? '',
            employeeChangeLast: employeeInfo?.empLast ?? '',
            employeeChangeEmail: employeeInfo?.empEmail ?? '',
            employeeChangePhone: employeeInfo?.empPhone ?? '',
            // Pickup and Delivery Info
            puAddress: pickupInfo?.puAddress,
            puAddress2: pickupInfo?.puAddress2,
            puCity: pickupInfo?.puCity,
            puStateProv: pickupInfo?.puStateProvince,
            puZipPostal: pickupInfo?.puZipPostal,
            doAddress: deliveryInfo?.doAddress,
            doAddress2: deliveryInfo?.doAddress2,
            doCity: deliveryInfo?.doCity,
            doStateProv: deliveryInfo?.doStateProvince,
            doZipPostal: deliveryInfo?.doZipPostal,
            pudoComments: locationComments?.pudoAddComments.replace(/'/g, '&quot'),
        };
        const response = await acBase.eraWebServiceCall<object>('SVPController', 'UpdateAppt', reqData, store, branch);
        let error: ApptError = { errorCode: '', errorMsg: ''};
        if (response) {
            if (response && (response as AxiosError).isAxiosError) {
                error = {
                    errorCode: (response as AxiosError).code ?? '',
                    errorMsg: (response as AxiosError).message,
                };
            } else if (response && response.errorCode) {
                error = {
                    errorCode: response.errorCode,
                    errorMsg: response.errorMsg,
                };
            }
        }
        dispatch(editAppt(appointment));
        dispatch(apptError(error));
    };
}

// Call PICK to cancel the appointment.
export function CancelAppt() {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const appointment = { ...getState().Appointment };
        const profileNum = getState().Framework.specProfile;
        const logId = getState().Specs.specs?.logId;
        const previewFlag = getState().Framework.previewFlag;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            // Service Requests
            logId: logId,
            clientId: 'RR',
            // Main appt data
            apptNumber: appointment.Appointment.apptNum,
            profileNum: profileNum,
            advNumber: appointment.Appointment.advisor,
            apptDate: appointment.Appointment.date,
            apptTime: appointment.Appointment.time,
            department: appointment.Appointment.department,
            apptFlag: '',
            vehPriority: '',
            vehPriorityPassword: '',
            transportationType: appointment.Appointment.transFlag,
            modifyJobsFlag: 'Y',
            allowOverbookFlag: 'Y',
            apptServiceLane: '',
            // VehicleData attributes
            vin: appointment.ApptVeh.vin,
            mileage: appointment.ApptVeh.mileage.toString(),
            // Appt CustomerData attributes
            custNumber: appointment.ApptCust.custNameId,
            // CustomerData attributes
            firstName: appointment.ApptCust.custFirstName.replace(/'/g, ''),
            lastName: appointment.ApptCust.custLastName.replace(/'/g, ''),
            emailAddress: appointment.ApptCust.custEmailAddress,
            // I individual
            IBFlag: 'I',
            // VehicleData attributes
            vehYear: appointment.ApptVeh.vehYr,
            vehMake: appointment.ApptVeh.make,
            vehModel: appointment.ApptVeh.model,
            // vehSeries: appointment.ApptVeh.series.replace(/'/g, '&quot'),
            vehColor: appointment.ApptVeh.vehColor,
            previewFlag: previewFlag,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<object>('SVPController', 'CancelAppt', reqData, store, branch);
        let error: ApptError = { errorCode: '', errorMsg: '' };
        if (response) {
            if (response && (response as AxiosError).isAxiosError) {
                error = {
                    errorCode: (response as AxiosError).code ?? '',
                    errorMsg: (response as AxiosError).message,
                };
            } else if (response && response.errorCode) {
                error = {
                    errorCode: response.errorCode,
                    errorMsg: response.errorMsg,
                };
            }
        }
        dispatch(cancelAppt(appointment));
        dispatch(apptError(error));
    };
}

// This function will update the Pickup information
export function UpdatePickupFields(inp: ApptPULoc) {
    return (dispatch) => {
        const PUInfo = {
            puAddress: inp.puAddress,
            puAddress2: inp.puAddress2,
            puCity: inp.puCity,
            puStateProvince: inp.puStateProvince,
            puZipPostal: inp.puZipPostal,
        };
        dispatch(updatePickupFields(PUInfo));
    };
}
// This function will update the Delivery information
export function UpdateDeliveryFields(inp: ApptDOLoc) {
    return (dispatch) => {
        const DOInfo = {
            doAddress: inp.doAddress,
            doAddress2: inp.doAddress2,
            doCity: inp.doCity,
            doStateProvince: inp.doStateProvince,
            doZipPostal: inp.doZipPostal,
        };
        dispatch(updateDeliveryFields(DOInfo));
    };
}
// This function updates the Additional Comments informtation
export function UpdateLocationComments(inp:ApptLocCom) {
    return (dispatch) => {
        const locComm = {
            pudoAddComments: inp.pudoAddComments,
        };
        dispatch(updateLocationComments(locComm));
    };
}
// This clears the information if the transportation type changes
export function clearPUDOInfo() {
    return (dispatch) => {
        dispatch(clearPuDoInfo());
    };
}
// Get city/state from provided Zip
export function ZIPSearch(zipCode:string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const locale = getState().Framework.locale;
        const logId = getState().Framework.logId;
        const callingProgram = 'SVP';
        const zip = zipCode;
        const reqData = {
            zipCode: zip,
            callingProgram: callingProgram,
            locale: locale,
            logId: logId,
        };
        const response = await acBase.eraWebServiceCall<ViewApptList>('SVPController', 'ZIPSearch', reqData, store, branch);
        let zipReturn = {city: '', state: ''};
        if (response !== null) {
            if (response.errorCode === '8000') {
                const error = {
                    errorCode: response.errorCode,
                    errorMsg: response.errorMsg,
                };
                dispatch(apptError(error));
            } else {
                if (response!) {
                    const CityArray = response.zipCity.toLowerCase().split(' ');
                    let city = '';
                    if (CityArray.length > 1) {
                        for (const word in CityArray) {
                            city += (CityArray[word].charAt(0).toUpperCase() + CityArray[word].slice(1) + ' ');
                        }
                    } else {
                        city = CityArray[0].charAt(0).toUpperCase() + CityArray[0].slice(1);
                    }

                    zipReturn = {
                        city: city,
                        state: response.zipState,
                    };
                }
                dispatch(zipSearch(zipReturn));
            }
        } else {
            zipReturn = {
                city: ' ',
                state: ' ',
            };
            dispatch(zipSearch(zipReturn));
        }
    };
}
// Clear ZIPSearch for future calls
export function clearZIPSearch() {
    return (dispatch) => {
        dispatch(clearZipSearch());
    };
}

export function SetChangeDeptFlag (inp: boolean) {
    return (dispatch) => {
        dispatch(setChangeDeptFlag(inp));
    };
}

export function ToggleApptLangLits(language: string) {
    return (dispatch, getState: () => IStoreState) => {
        let appointment = getState().Appointment.Appointment;
        if (appointment.advName === appointment.advisorOptionEN || appointment.advName === appointment.advisorOptionFR) {
            appointment = {
                ...appointment,
                advName: appointment.advisorOptionEN,
            };
            if (language === 'fr-CA') {
                appointment = {
                    ...appointment,
                    advName: appointment.advisorOptionFR,
                };
            }
        }
        dispatch(toggleApptLangLits(appointment));
    };
}

// Set selected id in log record
export function SetApptHistId(id: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        const callingProgram = 'SVP';
        const reqData = {
            custUserID: id,
            logId: logId,
            callingProgram: callingProgram,
        };
        // dispatch a tracking event
        dispatch(setApptHistIdTracking(true));
        const response = await acBase.eraWebServiceCall<AxiosError>(
            'SVPController', 'SetSelectedNum', reqData, store, branch);
        if (response && (response as AxiosError).isAxiosError) {
            const error = {
                errorCode: (response as AxiosError).code ?? '',
                errorMsg: (response as AxiosError).message,
            };
            dispatch(apptError(error));
        }
        dispatch(setApptHistId(id));
    };
}